import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAiQ } from '../state/AiQState';
import { useAuth } from '../../context/AuthContext';
import { fetchSentiment, fetchImpactAnalysis, SentimentResult } from '../services/NewsService';
import {
    IconGlobeAlt, IconZap, IconNews, IconRefresh
} from '../components/Icons';
import WhatsAppSendButton from '../components/WhatsAppSendButton';
import { Search, X } from 'lucide-react';
import toast from 'react-hot-toast';

const FEED_CACHE_KEY = 'aiq_news_feed';

const saveFeed = (feed: SentimentResult[]) => {
    try { localStorage.setItem(FEED_CACHE_KEY, JSON.stringify(feed)); } catch {}
};
const loadFeed = (): SentimentResult[] => {
    try {
        const raw = localStorage.getItem(FEED_CACHE_KEY);
        if (raw) return JSON.parse(raw);
    } catch {}
    return [];
};

const NewsIntelligenceView: React.FC = () => {
    const { trades } = useAiQ();
    const { currentUser } = useAuth();

    const [feed, setFeed] = useState<SentimentResult[]>(() => loadFeed());
    const [selectedTicker, setSelectedTicker] = useState<string | null>(
        () => { const f = loadFeed(); return f.length > 0 ? f[0].ticker : null; }
    );
    // Tracks which individual tickers are currently being refreshed
    const [refreshingTickers, setRefreshingTickers] = useState<Set<string>>(new Set());
    // Used only during the initial bulk portfolio load
    const [bulkLoading, setBulkLoading] = useState(false);
    const [impactAnalysis, setImpactAnalysis] = useState<any>(null);
    const [analyzingImpact, setAnalyzingImpact] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [searchingTicker, setSearchingTicker] = useState(false);

    // Persist feed to localStorage on every change
    useEffect(() => { saveFeed(feed); }, [feed]);

    // Top portfolio tickers by value
    const uniqueTickers = useMemo(() => {
        if (trades.length === 0) return [];
        const valueMap: Record<string, number> = {};
        trades.forEach((t: any) => {
            const sym = t.ticker || t.rawTicker;
            const qty = Number(t.quantity || t.shares || 0);
            const price = Number(t.price || 0);
            const val = t.valuation || (qty * price) || 0;
            if (sym && sym !== 'USD' && sym !== 'UNKNOWN') {
                valueMap[sym] = (valueMap[sym] || 0) + val;
            }
        });
        return Object.entries(valueMap)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([sym]) => sym);
    }, [trades]);

    // Initial bulk load for portfolio tickers (only when feed is empty)
    const loadPortfolioNews = async (tickers: string[]) => {
        if (!currentUser || tickers.length === 0) return;
        setBulkLoading(true);
        setFeed([]);
        setSelectedTicker(null);
        setImpactAnalysis(null);
        try {
            const token = await currentUser.getIdToken(true);
            for (const ticker of tickers) {
                try {
                    const result = await fetchSentiment(ticker, token);
                    if (result) {
                        setFeed(prev => {
                            const next = [...prev, result];
                            if (next.length === 1) setSelectedTicker(result.ticker);
                            return next;
                        });
                    }
                    await new Promise(r => setTimeout(r, 250));
                } catch (e) {
                    console.error(`Failed to load news for ${ticker}`, e);
                }
            }
        } catch (e) {
            console.error('Auth token failed', e);
        } finally {
            setBulkLoading(false);
        }
    };

    // Refresh a single ticker in-place
    const refreshOneTicker = async (ticker: string, e: React.MouseEvent) => {
        e.stopPropagation(); // don't select the row
        if (!currentUser || refreshingTickers.has(ticker)) return;
        setRefreshingTickers(prev => new Set(prev).add(ticker));
        try {
            const token = await currentUser.getIdToken(true);
            const result = await fetchSentiment(ticker, token);
            if (result) {
                setFeed(prev =>
                    prev.map(item => item.ticker === ticker ? result : item)
                );
                // If this was the selected ticker, clear stale impact analysis
                if (selectedTicker === ticker) setImpactAnalysis(null);
            } else {
                toast.error(`No news returned for ${ticker}`);
            }
        } catch {
            toast.error(`Failed to refresh ${ticker}`);
        } finally {
            setRefreshingTickers(prev => {
                const next = new Set(prev);
                next.delete(ticker);
                return next;
            });
        }
    };

    // Auto-load portfolio news once, only if cache is empty
    const autoLoaded = useRef(false);
    useEffect(() => {
        if (!autoLoaded.current && feed.length === 0 && uniqueTickers.length > 0 && currentUser) {
            autoLoaded.current = true;
            loadPortfolioNews(uniqueTickers);
        }
    }, [uniqueTickers, currentUser]);

    // Manual ticker search (adds new ticker to feed)
    const handleSearchTicker = async (e: React.FormEvent) => {
        e.preventDefault();
        const ticker = searchInput.trim().toUpperCase();
        if (!ticker || !currentUser) return;

        const existing = feed.find(i => i.ticker === ticker);
        if (existing) {
            setSelectedTicker(ticker);
            setImpactAnalysis(null);
            setSearchInput('');
            return;
        }

        setSearchingTicker(true);
        try {
            const token = await currentUser.getIdToken(true);
            const result = await fetchSentiment(ticker, token);
            if (result) {
                setFeed(prev => [result, ...prev]);
                setSelectedTicker(result.ticker);
                setImpactAnalysis(null);
                toast.success(`Loaded news for ${ticker}`);
            } else {
                toast.error(`No news data found for ${ticker}`);
            }
        } catch {
            toast.error(`Failed to load news for ${ticker}`);
        } finally {
            setSearchingTicker(false);
            setSearchInput('');
        }
    };

    // AI impact analysis for the selected ticker
    const runImpactAnalysis = async () => {
        if (!selectedTicker || !currentUser) return;
        const item = feed.find(i => i.ticker === selectedTicker);
        if (!item) return;
        setAnalyzingImpact(true);
        try {
            const token = await currentUser.getIdToken(true);
            const analysis = await fetchImpactAnalysis(selectedTicker, item.source_articles, token);
            setImpactAnalysis(analysis);
            if (analysis) toast.success('Impact analysis generated');
        } catch {
            toast.error('Analysis failed');
        } finally {
            setAnalyzingImpact(false);
        }
    };

    const getScoreColor = (score: number) => {
        if (score > 0.3) return 'bg-emerald-500';
        if (score < -0.3) return 'bg-red-500';
        return 'bg-slate-400';
    };

    const activeItem = feed.find(i => i.ticker === selectedTicker);

    return (
        <div className="flex h-full bg-slate-50 overflow-hidden">
            {/* Left: Feed */}
            <div className="w-80 bg-white border-r border-slate-200 flex flex-col">
                <div className="p-4 border-b border-slate-100">
                    <div className="flex justify-between items-center mb-3">
                        <h2 className="font-bold text-slate-700 flex items-center gap-2">
                            <IconGlobeAlt /> News Intelligence
                        </h2>
                        {bulkLoading && (
                            <span className="text-[10px] font-mono text-slate-400 animate-pulse">
                                Loading portfolio…
                            </span>
                        )}
                    </div>

                    {/* Manual ticker search */}
                    <form onSubmit={handleSearchTicker} className="flex gap-2">
                        <div className="flex-1 flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                            <Search size={13} className="text-slate-400 shrink-0"/>
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value.toUpperCase())}
                                placeholder="Search any ticker…"
                                className="flex-1 text-xs bg-transparent outline-none text-slate-700 placeholder:text-slate-400 uppercase font-medium"
                            />
                            {searchInput && (
                                <button type="button" onClick={() => setSearchInput('')} className="text-slate-300 hover:text-slate-500">
                                    <X size={12}/>
                                </button>
                            )}
                        </div>
                        <button
                            type="submit"
                            disabled={!searchInput.trim() || searchingTicker}
                            className="px-3 py-1.5 bg-slate-800 text-white rounded-lg text-xs font-bold disabled:opacity-40 hover:bg-slate-700 transition-colors shrink-0"
                        >
                            {searchingTicker
                                ? <span className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin inline-block"/>
                                : 'Go'
                            }
                        </button>
                    </form>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {feed.map(item => {
                        const isTickerRefreshing = refreshingTickers.has(item.ticker);
                        const isSelected = selectedTicker === item.ticker;
                        return (
                            <div
                                key={item.ticker}
                                onClick={() => { setSelectedTicker(item.ticker); setImpactAnalysis(null); }}
                                className={`group p-4 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                                    isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                                }`}
                            >
                                <div className="flex justify-between items-center mb-1">
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-slate-800">{item.ticker}</span>
                                        {/* Per-ticker refresh button — visible on hover or while loading */}
                                        <button
                                            onClick={(e) => refreshOneTicker(item.ticker, e)}
                                            disabled={isTickerRefreshing}
                                            className={`transition-opacity ${
                                                isTickerRefreshing
                                                    ? 'opacity-100 text-blue-500'
                                                    : 'opacity-30 group-hover:opacity-100 text-slate-400 hover:text-blue-500'
                                            }`}
                                            title={`Refresh ${item.ticker} news`}
                                        >
                                            <IconRefresh
                                                size={12}
                                                className={isTickerRefreshing ? 'animate-spin' : ''}
                                            />
                                        </button>
                                    </div>
                                    <span className={`text-[10px] text-white px-1.5 rounded ${getScoreColor(item.overall_score)}`}>
                                        {item.overall_score > 0 ? '+' : ''}{item.overall_score.toFixed(2)}
                                    </span>
                                </div>
                                <p className="text-xs text-slate-500 line-clamp-2 leading-snug">{item.summary}</p>
                            </div>
                        );
                    })}

                    {/* Placeholder skeleton during initial bulk load */}
                    {bulkLoading && (
                        <div className="p-4 border-b border-slate-100 opacity-50">
                            <div className="h-4 bg-slate-200 rounded w-1/4 animate-pulse mb-2"/>
                            <div className="h-3 bg-slate-200 rounded w-3/4 animate-pulse"/>
                        </div>
                    )}

                    {feed.length === 0 && !bulkLoading && (
                        <div className="p-8 text-center">
                            <IconNews size={32} className="mx-auto mb-2 opacity-20 text-slate-400"/>
                            <p className="font-medium text-slate-500 text-sm">No news loaded</p>
                            <p className="text-xs mt-2 text-slate-400 mb-4">
                                Search any ticker above, or load your portfolio's news.
                            </p>
                            {uniqueTickers.length > 0 && (
                                <button
                                    onClick={() => loadPortfolioNews(uniqueTickers)}
                                    className="px-3 py-1.5 bg-slate-800 text-white rounded-lg text-xs font-bold hover:bg-slate-700 transition-colors"
                                >
                                    Load Portfolio News
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Right: Detail */}
            <div className="flex-1 flex flex-col h-full overflow-hidden bg-slate-50">
                {activeItem ? (
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        <div className="flex justify-between items-start">
                            <div>
                                <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                                    {activeItem.ticker}
                                    <span className="text-sm font-normal text-slate-500 bg-white px-2 py-1 rounded border border-slate-200">
                                        Market Sentiment Analysis
                                    </span>
                                </h1>
                                <p className="text-slate-500 mt-1">{activeItem.summary}</p>
                            </div>

                            <div className="flex items-center gap-2 shrink-0">
                                <WhatsAppSendButton
                                    label={`${activeItem.ticker} News`}
                                    content={`${activeItem.ticker} — Market Sentiment\n\n${activeItem.summary}`}
                                    variant="ghost"
                                />
                                <button
                                    onClick={runImpactAnalysis}
                                    disabled={analyzingImpact}
                                    className="bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-lg hover:bg-slate-800 flex items-center gap-2 transition-all disabled:opacity-50"
                                >
                                    {analyzingImpact ? (
                                        <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/> Analyzing...</>
                                    ) : (
                                        <><IconZap size={16} className="text-yellow-400"/> AI Impact Check</>
                                    )}
                                </button>
                            </div>
                        </div>

                        {impactAnalysis && (
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-blue-100 relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"/>
                                <h3 className="text-blue-600 font-bold uppercase text-xs tracking-wider mb-2 flex items-center gap-2">
                                    <IconZap size={14}/> AI Insight: {impactAnalysis.most_impactful_event}
                                </h3>
                                <div
                                    className="prose prose-sm max-w-none text-slate-700"
                                    dangerouslySetInnerHTML={{ __html: impactAnalysis.potential_impact }}
                                />
                            </div>
                        )}

                        <div>
                            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Source Articles</h3>
                            <div className="grid grid-cols-1 gap-3">
                                {activeItem.source_articles.map((article, idx) => (
                                    <a
                                        key={idx}
                                        href={article.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block bg-white p-4 rounded-lg border border-slate-200 hover:shadow-md transition-shadow group"
                                    >
                                        <div className="flex justify-between items-start">
                                            <h4 className="font-medium text-slate-800 group-hover:text-blue-600 transition-colors line-clamp-2">
                                                {article.title}
                                            </h4>
                                            <span className="text-[10px] text-slate-400 shrink-0 ml-4">
                                                {new Date(article.publishedAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                        {article.description && (
                                            <p className="text-xs text-slate-500 mt-2 line-clamp-2">{article.description}</p>
                                        )}
                                        <div className="mt-2">
                                            <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                                                {article.source.name || 'News Wire'}
                                            </span>
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="text-center text-slate-400">
                            <IconNews size={64} className="mx-auto mb-4 opacity-20"/>
                            <p className="text-lg font-medium">Select a ticker to view news</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NewsIntelligenceView;
